import { LigneDevis, TypeLigneDevis, Devis, TypeRemise } from '../types';

/**
 * Calcule la surface d'une ligne de devis de type SURFACE
 */
export const calculateSurface = (longueur: number, largeur: number, avecPertes: boolean = false): number => {
  // Convertir en nombre pour éviter les erreurs de type
  const l = Number(longueur) || 0;
  const L = Number(largeur) || 0;
  const surface = l * L;
  // Ajouter 10% de pertes si activé
  return avecPertes ? surface * 1.1 : surface;
};

/**
 * Calcule le total d'une ligne de devis
 */
export const calculateLigneTotal = (ligne: LigneDevis): number => {
  // Convertir prixUnitaire en nombre
  const prixUnitaire = Number(ligne.prixUnitaire) || 0;

  switch (ligne.type) {
    case TypeLigneDevis.SURFACE:
      let surfaceFinale = 0;

      // Mode saisie par dimensions (L × l)
      if (ligne.modeSaisieSurface === 'dimensions' || !ligne.modeSaisieSurface) {
        if (ligne.longueur && ligne.largeur) {
          surfaceFinale = calculateSurface(ligne.longueur, ligne.largeur, ligne.avecPertes);
        }
      }
      // Mode saisie directe de la surface
      else if (ligne.modeSaisieSurface === 'direct') {
        if (ligne.surface) {
          const surface = Number(ligne.surface) || 0;
          surfaceFinale = ligne.avecPertes ? surface * 1.1 : surface;
        }
      }

      return surfaceFinale * prixUnitaire;

    case TypeLigneDevis.UNITAIRE:
    case TypeLigneDevis.PIECE:
      // Pour les unitaires et les pièces, c'est quantité * prix unitaire
      const quantite = Number(ligne.quantite) || 0;
      return quantite * prixUnitaire;

    case TypeLigneDevis.FORFAIT:
      // Pour les forfaits, c'est directement le prix unitaire
      return prixUnitaire;

    default:
      return 0;
  }
};

/**
 * Calcule le montant de la remise
 */
export const calculateRemise = (
  montantHT: number,
  typeRemise?: TypeRemise,
  valeurRemise?: number
): number => {
  if (!typeRemise || !valeurRemise || valeurRemise <= 0) {
    return 0;
  }

  const montant = Number(montantHT) || 0;
  const valeur = Number(valeurRemise) || 0;

  switch (typeRemise) {
    case TypeRemise.POURCENTAGE:
      // Remise en pourcentage (ex: 10% de 1000€ = 100€)
      return Math.round((montant * valeur / 100) * 100) / 100;

    case TypeRemise.MONTANT_FIXE:
      // Remise en montant fixe (ex: 150€)
      // S'assurer que la remise ne dépasse pas le montant HT
      return Math.min(valeur, montant);

    default:
      return 0;
  }
};

/**
 * Calcule tous les totaux d'un devis
 */
export const calculateDevisTotals = (
  lignes: LigneDevis[],
  tauxTVA: number = 20
): {
  sousTotal: number;
  montantTVA: number;
  totalTTC: number;
} => {
  // Convertir tauxTVA en nombre (gérer le cas où tauxTVA = 0)
  const tva = tauxTVA !== undefined && tauxTVA !== null ? Number(tauxTVA) : 0;

  // Calcul du sous-total (somme de toutes les lignes + sous-lignes)
  const sousTotal = lignes.reduce((acc, ligne) => {
    // Ajouter le total de la ligne principale
    let total = acc + calculateLigneTotal(ligne);

    // Ajouter les totaux des sous-lignes (consommables)
    if (ligne.sousLignes && ligne.sousLignes.length > 0) {
      const totalSousLignes = ligne.sousLignes.reduce((sousAcc, sousLigne) => {
        return sousAcc + calculateLigneTotal(sousLigne);
      }, 0);
      total += totalSousLignes;
    }

    return total;
  }, 0);

  // Calcul de la TVA
  const montantTVA = (sousTotal * tva) / 100;

  // Calcul du total TTC
  const totalTTC = sousTotal + montantTVA;

  return {
    sousTotal: Math.round(sousTotal * 100) / 100, // Arrondi à 2 décimales
    montantTVA: Math.round(montantTVA * 100) / 100,
    totalTTC: Math.round(totalTTC * 100) / 100,
  };
};

/**
 * Calcule tous les totaux d'un devis avec remise
 */
export const calculateDevisTotalsWithRemise = (
  lignes: LigneDevis[],
  tauxTVA: number = 20,
  typeRemise?: TypeRemise,
  valeurRemise?: number
): {
  sousTotal: number;
  montantRemise: number;
  montantHTApresRemise: number;
  montantTVA: number;
  totalTTC: number;
} => {
  // Calcul du sous-total
  const { sousTotal } = calculateDevisTotals(lignes, tauxTVA);

  // Calcul de la remise
  const montantRemise = calculateRemise(sousTotal, typeRemise, valeurRemise);

  // Montant HT après remise
  const montantHTApresRemise = Math.round((sousTotal - montantRemise) * 100) / 100;

  // Calcul de la TVA sur le montant après remise (gérer le cas où tauxTVA = 0)
  const tva = tauxTVA !== undefined && tauxTVA !== null ? Number(tauxTVA) : 0;
  const montantTVA = Math.round((montantHTApresRemise * tva / 100) * 100) / 100;

  // Calcul du total TTC
  const totalTTC = Math.round((montantHTApresRemise + montantTVA) * 100) / 100;

  return {
    sousTotal: Math.round(sousTotal * 100) / 100,
    montantRemise: Math.round(montantRemise * 100) / 100,
    montantHTApresRemise,
    montantTVA,
    totalTTC,
  };
};

/**
 * Recalcule un devis complet avec tous ses totaux
 */
export const recalculateDevis = (devis: Devis): Devis => {
  const totals = calculateDevisTotals(devis.lignes, devis.tauxTVA);

  return {
    ...devis,
    ...totals,
  };
};

/**
 * Formate un montant en euros
 */
export const formatEuro = (amount: number | undefined | null): string => {
  // Gérer les valeurs invalides (NaN, undefined, null)
  const validAmount = Number(amount) || 0;

  // Vérifier si c'est un nombre valide
  if (!isFinite(validAmount)) {
    return '0,00 €';
  }

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(validAmount);
};

/**
 * Formate une date au format français
 */
export const formatDate = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('fr-FR').format(dateObj);
};

/**
 * Transforme une ligne de devis du format backend vers le format frontend
 */
const transformBackendLigne = (backendLigne: any): LigneDevis => {
  // Déterminer le type en fonction de l'unité
  let type: TypeLigneDevis = TypeLigneDevis.UNITAIRE;
  let modeSaisieSurface: 'dimensions' | 'direct' | undefined = undefined;
  let surface: number | undefined = undefined;

  if (backendLigne.unite === 'm²') {
    type = TypeLigneDevis.SURFACE;
    modeSaisieSurface = 'direct';
    surface = Number(backendLigne.quantite) || 0;
  } else if (backendLigne.unite === 'forfait') {
    type = TypeLigneDevis.FORFAIT;
  } else {
    type = TypeLigneDevis.UNITAIRE;
  }

  // Calculer le total de la ligne
  const prixUnitaire = Number(backendLigne.prixUnitaireHT) || 0;
  const quantite = Number(backendLigne.quantite) || 1;
  const total = prixUnitaire * quantite;

  // Transformer les sous-lignes si elles existent
  const sousLignes: LigneDevis[] = [];
  if (backendLigne.sousLignes && Array.isArray(backendLigne.sousLignes)) {
    backendLigne.sousLignes.forEach((sousLigne: any) => {
      const sousLigneTransformee: LigneDevis = {
        id: sousLigne.id?.toString() || `sous_ligne_${Date.now()}_${Math.random()}`,
        description: sousLigne.description || '',
        type: TypeLigneDevis.UNITAIRE,
        quantite: Number(sousLigne.quantite) || 0,
        prixUnitaire: Number(sousLigne.prixUnitaireHT) || 0,
        total: (Number(sousLigne.quantite) || 0) * (Number(sousLigne.prixUnitaireHT) || 0),
        estSousLigne: true,
        parentId: backendLigne.id?.toString(),
      };
      sousLignes.push(sousLigneTransformee);
    });
  }

  return {
    id: backendLigne.id?.toString() || `ligne_${Date.now()}_${Math.random()}`,
    description: backendLigne.description || '',
    type,
    modeSaisieSurface,
    quantite,
    prixUnitaire,
    total,
    surface,
    longueur: undefined,
    largeur: undefined,
    avecPertes: false,
    sousLignes: sousLignes.length > 0 ? sousLignes : undefined,
  };
};

/**
 * Normalise un devis reçu du backend pour s'assurer que toutes les valeurs numériques sont valides
 * Les sous-lignes sont déjà incluses dans les lignes retournées par le backend
 */
export const normalizeDevis = (devis: any): Devis => {
  // Transformer les lignes du backend vers le format frontend
  // La transformation inclut automatiquement les sous-lignes
  const lignes: LigneDevis[] = (devis.lignes || []).map((ligne: any) => transformBackendLigne(ligne));

  // Récupérer les montants du backend (ils sont déjà calculés correctement)
  const montantHT = Number(devis.montantHT) || 0;
  const montantTVA = Number(devis.montantTVA) || 0;
  const totalTTC = Number(devis.montantTTC) || 0;
  const tauxTVA = devis.tauxTVA !== undefined && devis.tauxTVA !== null ? Number(devis.tauxTVA) : 20;

  // Récupérer les informations de remise si présentes
  const typeRemise = devis.typeRemise as TypeRemise | undefined;
  const valeurRemise = devis.valeurRemise ? Number(devis.valeurRemise) : undefined;
  const montantRemise = devis.montantRemise ? Number(devis.montantRemise) : undefined;
  const montantHTApresRemise = devis.montantHTApresRemise ? Number(devis.montantHTApresRemise) : undefined;

  // Si les montants du backend sont valides, les utiliser
  if (isFinite(montantHT) && isFinite(montantTVA) && isFinite(totalTTC) &&
      montantHT > 0 && totalTTC > 0) {
    return {
      ...devis,
      lignes,
      montantHT,
      sousTotal: montantHT,
      montantTVA,
      totalTTC,
      tauxTVA,
      typeRemise,
      valeurRemise,
      montantRemise,
      montantHTApresRemise,
    };
  }

  // Sinon, recalculer les montants à partir des lignes transformées
  const totals = calculateDevisTotals(lignes, tauxTVA);

  return {
    ...devis,
    lignes,
    sousTotal: totals.sousTotal,
    montantHT: totals.sousTotal,
    montantTVA: totals.montantTVA,
    totalTTC: totals.totalTTC,
    tauxTVA,
    typeRemise,
    valeurRemise,
    montantRemise,
    montantHTApresRemise,
  };
};
